/**
 * Campaigns > Templates — the `/api/private/outreach-templates` resource (mb-frontend#4638
 * phase 2). Distinct from any DM/message-snippet template resource; this is the multi-step
 * outreach-sequence template CRUD.
 *
 * Every shape here is the generated type from mb-server's zod contract; nothing is retyped
 * by hand.
 */

import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { apiInstance } from '@/utils/axios';
import type {
  TemplateListItem,
  TemplateDetail,
  TemplateRosterItem,
  TemplateSuggestion,
  TemplatePreviewResponse,
  TemplateSampleResponse,
  TemplateVariablesResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
} from '@/types/generated/templates';

export type {
  TemplateStep,
  TemplateListItem,
  TemplateDetail,
  TemplateRosterItem,
  TemplateSuggestion,
  TemplatePreviewResponse,
  TemplateVariable,
  CreateTemplateRequest,
  UpdateTemplateRequest,
} from '@/types/generated/templates';

export interface OutreachTemplateRoster {
  items: TemplateRosterItem[];
  /** audience-processor's Jev pick (mb-frontend#4638 phase 2, A5), resolved for this viewer
   * by mb-server (S21). `state: 'not_decided'` when nothing has been computed yet, the pick
   * failed, or it no longer resolves to a current roster item. */
  suggestion: TemplateSuggestion;
  /** Board 2a's state-c fallback (S27): only computed when suggestion.state is 'not_decided'.
   * The workspace's most recently enrolled-through roster template, any team member, any time —
   * null when nobody ever has, in which case the roster's first entry still stands in. */
  mostRecentlyUsedTemplateId: number | null;
}

export async function listOutreachTemplates(): Promise<TemplateListItem[]> {
  const response = await apiInstance({ url: API_ENDPOINTS.outreachTemplates, method: REQUEST_METHOD.GET });
  return response.data.items;
}

/**
 * What a roster is for: a signal, or an account with no signal row yet — then the type and
 * company Enroll will create the signal with, as the confirm's preview names them (board 1c).
 */
export type RosterSource = (
  { signalId: string } | { signalType: string; companyDomain: string | null }
) & { onBehalfOf?: string | null };

export const rosterSourceKey = (source: RosterSource): string => {
  const subject = 'signalId' in source ? source.signalId : `account:${source.signalType}:${(source.companyDomain ?? '').toLowerCase()}`;
  return source.onBehalfOf ? `${subject}:as:${source.onBehalfOf}` : subject;
};

/** The confirm-line dropdown's data source: what the viewer can enroll one signal into. */
export async function getOutreachTemplateRoster(source: RosterSource): Promise<OutreachTemplateRoster> {
  // An admin viewing a rep's signals sees that rep's templates (their versions).
  const actingFor = source.onBehalfOf ? { onBehalfOf: source.onBehalfOf } : {};
  const params = 'signalId' in source
    ? { signalId: source.signalId, ...actingFor }
    : { signalType: source.signalType, ...(source.companyDomain ? { companyDomain: source.companyDomain } : {}), ...actingFor };
  const response = await apiInstance({
    url: API_ENDPOINTS.outreachTemplateRoster,
    method: REQUEST_METHOD.GET,
    params,
  });
  return response.data;
}

export async function getOutreachTemplate(templateId: number): Promise<TemplateDetail> {
  const response = await apiInstance({ url: API_ENDPOINTS.outreachTemplate(templateId), method: REQUEST_METHOD.GET });
  return response.data;
}

/**
 * `signalType` is "try another person" — S18: preview against a different one of the
 * template's own appliesTo signal types instead of the default fallback pick. Takes priority
 * over signalId server-side when both are given, so callers should pass only one.
 */
export async function getOutreachTemplatePreview(
  templateId: number,
  { signalId, signalType, previewIndex }: { signalId?: string; signalType?: string; previewIndex?: number } = {},
): Promise<TemplatePreviewResponse> {
  const params = {
    ...(signalType ? { signalType } : signalId ? { signalId } : undefined),
    ...(previewIndex !== undefined ? { previewIndex } : undefined),
  };
  const response = await apiInstance({
    url: API_ENDPOINTS.outreachTemplatePreview(templateId),
    method: REQUEST_METHOD.GET,
    params,
  });
  return response.data;
}

/**
 * Board 4 "Generate sample": asks mb-server to have the content worker write this person's
 * AI steps for this template — the same request (and the same stored drafts) a direct
 * enrollment of this person makes. Billed like any draft, so only on an explicit click. Poll
 * the preview: each AI step's `sample` fills in once written (writtenAt >= requestedAt).
 */
export async function requestOutreachTemplateSample(templateId: number, personaId: string): Promise<TemplateSampleResponse> {
  const response = await apiInstance({
    url: API_ENDPOINTS.outreachTemplatePreviewSample(templateId),
    method: REQUEST_METHOD.POST,
    data: { personaId },
  });
  return response.data;
}

/**
 * The {variable}s a fixed-copy step can fill (mb-frontend#5225). The server's list, not a copy of
 * it here: the renderer that fills these tokens is mb-server's, so a hardcoded list in the UI
 * would drift the moment that renderer learns a variable. Workspace-independent, so it is fetched
 * once and cached for the session.
 */
export async function getOutreachTemplateVariables(): Promise<TemplateVariablesResponse> {
  const response = await apiInstance({ url: API_ENDPOINTS.outreachTemplateVariables, method: REQUEST_METHOD.GET });
  return response.data;
}

export async function createOutreachTemplate(body: CreateTemplateRequest): Promise<TemplateDetail> {
  const response = await apiInstance({ url: API_ENDPOINTS.outreachTemplates, method: REQUEST_METHOD.POST, data: body });
  return response.data;
}

export async function updateOutreachTemplate(templateId: number, body: UpdateTemplateRequest): Promise<TemplateDetail> {
  const response = await apiInstance({ url: API_ENDPOINTS.outreachTemplate(templateId), method: REQUEST_METHOD.PUT, data: body });
  return response.data;
}

export async function deleteOutreachTemplate(templateId: number): Promise<void> {
  await apiInstance({ url: API_ENDPOINTS.outreachTemplate(templateId), method: REQUEST_METHOD.DELETE });
}
